import { ImageType } from "@/components/ui/tree/tree-context-menu/actions/upload-action";
import { selectCurrentUser } from "@/store/user-selectors";
import {
  selectChildDepthFirst,
  selectIElement,
  selectRootId,
} from "@faro-lotv/app-component-toolbox";
import { parseImageFromFile, removeExtension } from "@faro-lotv/flat-ui";
import { assert, generateGUID, GUID } from "@faro-lotv/foundation";
import {
  createIElement,
  IElementGroup,
  IElementImg2d,
  IElementImg360,
  IElementSection,
  IElementType,
  IElementTypeHint,
  isIElementAttachmentsGroup,
  isIElementRoomSection,
  isIElementSection,
  isIElementTimeSeriesRoom,
} from "@faro-lotv/ielement-types";
import {
  createMutationAdd2dAttachment,
  createMutationAddImg360ToRoom,
  MutationAdd2dAttachment,
  MutationAddImg360ToRoom,
  ProjectApi,
  UploadedFileData,
} from "@faro-lotv/service-wires";
import { parse as exifParse } from "exifr";
import { updateProject } from "../project-provider/update-project";
import { groupImageFilesByHeuristic } from "./image-type-heuristic";
import {
  CommonUpdateProjectMultiFilesParams,
  UploadElementType,
} from "./use-upload-element-types";

export type UpdateProjectWithImageDataParams = {
  type: UploadElementType.imageData;

  /** The type of image to be uploaded */
  imgType: ImageType;

  /** ID of the parent element (for example section room Id for 360Image) */
  parentElementId: GUID;
};

/**
 * Update project with uploaded multiple 360 images or 2D images
 *
 * @param params parameters to be used to update the project
 * @returns Promise<void>
 */
export function updateProjectWithImageData(
  params: CommonUpdateProjectMultiFilesParams &
    UpdateProjectWithImageDataParams,
): Promise<void> {
  switch (params.imgType) {
    case ImageType.auto:
      return updateProjectWithPhotos(params);
    case ImageType.Img360:
      return updateTimeSeriesWith360Imgs(params);
    case ImageType.Img2D:
      return updateLocationWith2DPhotos(params);
  }
}

async function updateProjectWithPhotos({
  appStore,
  dispatch,
  projectApi,
  parentElementId,
  files,
  uploadedFilesData,
  additionalUrls,
}: CommonUpdateProjectMultiFilesParams &
  UpdateProjectWithImageDataParams): Promise<void> {
  // ensure number of files equals number of uploaded data
  assert(
    files.length === uploadedFilesData.length &&
      additionalUrls.length === files.length,
    "Number of files and uploaded data must match",
  );

  // Get root ID and user ID from global store
  const rootId = selectRootId(appStore.getState());
  const userId = selectCurrentUser(appStore.getState())?.id;

  // Ensure a valid user is logged in
  assert(userId, "user ID is not specified");

  // Fetch the parent section from state by its ID
  const section = selectIElement(parentElementId)(appStore.getState());

  // Ensure valid root/section structure and that section is a proper section element
  assert(
    rootId && section && isIElementSection(section),
    "invalid project structure",
  );

  // Find the associated time series room section under the parent section
  const timeSeries = selectChildDepthFirst(
    section,
    isIElementTimeSeriesRoom,
  )(appStore.getState());

  // Ensure a valid time series room is found
  assert(timeSeries, "invalid time series to insert pano 360 image");

  const { img360s, img2ds } = await groupImageFilesByHeuristic(
    files,
    uploadedFilesData,
    additionalUrls,
  );

  // Find the group of attachments under the parent section
  const attachmentsGroup = selectChildDepthFirst(
    section,
    isIElementAttachmentsGroup,
  )(appStore.getState());

  const attachmentGroupId = attachmentsGroup?.id ?? generateGUID();

  const mutations = await Promise.all([
    ...img360s.map(({ file, uploadedFileData, thumbnailUri }, index) =>
      createMutationAddImg360ToRoomFromFile({
        file,
        downloadUrl: uploadedFileData.downloadUrl,
        thumbnailUri,
        md5Hash: uploadedFileData.md5,
        isFirstPanoInRoom: !timeSeries.childrenIds?.length && index === 0,
        projectApi,
        rootId,
        userId,
        timeSeriesId: timeSeries.id,
      }),
    ),

    ...img2ds.map(({ file, uploadedFileData, thumbnailUri }, index) =>
      createMutationAdd2dAttachmentFromFile({
        file,
        uploadedFileData,
        thumbnailUri,
        rootId,
        userId,
        attachmentGroupId,
        shouldCreateAttachmentsGroup: !attachmentsGroup && index === 0,
        roomSectionId: section.id,
      }),
    ),
  ]);

  // Apply mutations
  await projectApi.applyMutations(mutations);

  /**
   * Refresh the local project store:
   * Re-fetch all IElements under the original section to reflect the newly added pano.
   */
  await dispatch(
    updateProject({
      projectApi,
      iElementQuery: {
        ancestorIds: [section.id],
      },
    }),
  );
}

/**
 * Inserts multiple new 360° images into the appropriate time series room within the project.
 * If the parent section represents a location marker (i.e., the first pano),
 * a new "room" section is created to contain the image.
 * Otherwise, a "timeTravel" section is created to represent an additional pano in the sequence.
 */
async function updateTimeSeriesWith360Imgs({
  appStore,
  dispatch,
  projectApi,
  parentElementId,
  files,
  uploadedFilesData,
  additionalUrls,
}: CommonUpdateProjectMultiFilesParams &
  UpdateProjectWithImageDataParams): Promise<void> {
  // ensure number of files equals number of uploaded data
  assert(
    files.length === uploadedFilesData.length,
    "Number of files and uploaded data must match",
  );

  // Get root ID and user ID from global store
  const rootId = selectRootId(appStore.getState());
  const userId = selectCurrentUser(appStore.getState())?.id;

  // Ensure a valid user is logged in
  assert(userId, "user ID is not specified");

  // Fetch the parent section from state by its ID
  const section = selectIElement(parentElementId)(appStore.getState());

  // Ensure valid root/section structure and that section is a proper section element
  assert(
    rootId && section && isIElementSection(section),
    "invalid project structure",
  );

  // Find the associated time series room section under the parent section
  const timeSeries = selectChildDepthFirst(
    section,
    isIElementTimeSeriesRoom,
  )(appStore.getState());

  // Ensure a valid time series room is found
  assert(timeSeries, "invalid time series to insert pano 360 image");

  const mutations = await Promise.all(
    files.map((file, index) =>
      createMutationAddImg360ToRoomFromFile({
        file,
        downloadUrl: uploadedFilesData[index].downloadUrl,
        thumbnailUri: additionalUrls[index],
        md5Hash: uploadedFilesData[index].md5,
        isFirstPanoInRoom: !timeSeries.childrenIds?.length && index === 0,
        projectApi,
        rootId,
        userId,
        timeSeriesId: timeSeries.id,
      }),
    ),
  );

  // Apply mutations
  await projectApi.applyMutations(mutations);

  /**
   * Refresh the local project store:
   * Re-fetch all IElements under the original section to reflect the newly added pano.
   */
  await dispatch(
    updateProject({
      projectApi,
      iElementQuery: {
        ancestorIds: [section.id],
      },
    }),
  );
}

type CreateMutationAddImg360ToRoomFromFileProps = {
  /** The file that was uploaded */
  file: File;
  /** The download URL of the uploaded file */
  downloadUrl: string;
  /** Uri of a thumbnail image */
  thumbnailUri: string;
  /** The MD5 hash of the uploaded file */
  md5Hash: string;
  /** Whether this is the first panorama in the room. Needed to set the correct type hint */
  isFirstPanoInRoom: boolean;
  /** The project API instance */
  projectApi: ProjectApi;
  /** The root ID of the project */
  rootId: GUID;
  /** The user ID of the uploader */
  userId: GUID;
  /** The time series ID to add the image to */
  timeSeriesId: GUID;
};

/**
 * @returns The mutation to add a new 360° image to the specified time series room.
 */
export async function createMutationAddImg360ToRoomFromFile({
  file,
  downloadUrl,
  md5Hash,
  thumbnailUri,
  isFirstPanoInRoom,
  projectApi,
  rootId,
  userId,
  timeSeriesId,
}: CreateMutationAddImg360ToRoomFromFileProps): Promise<MutationAddImg360ToRoom> {
  const imageData = await getImageMetadata(file);
  const { width: imageWidth, height: imageHeight } =
    await parseImageFromFile(file);
  // Slice the panorama image into multiple levels of detail and retrieve the resulting level of details information
  const lods = await projectApi.slicePanoImage({
    originalImgUri: downloadUrl,
  });

  const { name } = file;
  const panoParentElement: IElementSection = {
    type: IElementType.section,
    typeHint: isFirstPanoInRoom
      ? IElementTypeHint.room
      : IElementTypeHint.timeTravel,
    id: generateGUID(),
    name: removeExtension(name),
    rootId,
    root_Id: rootId,
    parentId: timeSeriesId,
    createdBy: userId,
    createdAt: imageData.captureDate.toISOString(),
    modifiedBy: userId,
    modifiedAt: imageData.captureDate.toISOString(),
  };
  const newImageElement: IElementImg360 = {
    type: IElementType.img360,
    md5Hash,
    thumbnailUri,
    json1x1: "",
    fileSize: file.size,
    pixelWidth: imageWidth,
    pixelHeight: imageHeight,
    levelsOfDetail: lods.levelOfDetails,
    fileName: name,
    uri: downloadUrl,
    id: generateGUID(),
    rootId,
    root_Id: rootId,
    parentId: panoParentElement.id,
    typeHint: null,
    name,
    createdBy: userId,
    createdAt: imageData.captureDate.toISOString(),
    modifiedBy: userId,
    modifiedAt: imageData.captureDate.toISOString(),
    pose:
      imageData.latitude && imageData.longitude
        ? {
            pos: null,
            scale: null,
            isWorldRot: false,
            rot: null,
            gps: {
              gpsPos: {
                type: "Point",
                coordinates: [imageData.longitude, imageData.latitude],
              },
              altitude: null,
              latLongAcc: 0,
              altiAcc: null,
            },
          }
        : undefined,
  };

  return createMutationAddImg360ToRoom(
    timeSeriesId,
    panoParentElement,
    newImageElement,
  );
}

/**
 * Inserts multiple new 2D photo attachments into the given room section location.
 * If the room section does NOT contain element of attachments group,
 * a new attachments group will be constructed and passed to the mutation.
 */
async function updateLocationWith2DPhotos({
  appStore,
  dispatch,
  projectApi,
  parentElementId,
  files,
  uploadedFilesData,
  additionalUrls,
}: CommonUpdateProjectMultiFilesParams &
  UpdateProjectWithImageDataParams): Promise<void> {
  // ensure number of files equals number of uploaded data
  assert(
    files.length === uploadedFilesData.length &&
      files.length === additionalUrls.length,
    "Number of files and uploaded data must match",
  );

  // Get root ID and user ID from global store
  const rootId = selectRootId(appStore.getState());
  const userId = selectCurrentUser(appStore.getState())?.id;

  // Ensure a valid user is logged in
  assert(userId, "user ID is not specified");

  // Fetch the parent room section from state by its ID
  const section = selectIElement(parentElementId)(appStore.getState());

  // Ensure valid root/section structure and that section is a room section element
  assert(
    rootId && section && isIElementRoomSection(section),
    "invalid project structure",
  );

  // Find the group of attachments under the parent section
  const attachmentsGroup = selectChildDepthFirst(
    section,
    isIElementAttachmentsGroup,
  )(appStore.getState());

  const attachmentGroupId = attachmentsGroup?.id ?? generateGUID();

  const mutations = await Promise.all(
    files.map((file, index) =>
      createMutationAdd2dAttachmentFromFile({
        file,
        uploadedFileData: uploadedFilesData[index],
        thumbnailUri: additionalUrls[index],
        rootId,
        userId,
        attachmentGroupId,
        shouldCreateAttachmentsGroup: !attachmentsGroup && index === 0,
        roomSectionId: section.id,
      }),
    ),
  );

  // Apply mutations
  await projectApi.applyMutations(mutations);

  /**
   * Refresh the local project store:
   * Re-fetch all IElements under the original section to reflect the newly added photo.
   */
  await dispatch(
    updateProject({
      projectApi,
      iElementQuery: {
        ancestorIds: [section.id],
      },
    }),
  );
}

type CreateMutationAdd2dAttachmentFromFileProps = {
  /** The file that was uploaded */
  file: File;
  /** The uploaded file data */
  uploadedFileData: UploadedFileData;
  /** The download URL of the thumbnail file */
  thumbnailUri: string;
  /** The ID of the room section to add the image to */
  roomSectionId: GUID;
  /** Whether a new attachments group should be created */
  shouldCreateAttachmentsGroup: boolean;
  /** Id of either an existing or a new attachments group to create */
  attachmentGroupId: GUID;
  /** The root ID of the project */
  rootId: GUID;
  /** The user ID of the uploader */
  userId: GUID;
  /** Optional Id of the new Img2D element */
  newImg2dId?: GUID;
};

/**
 * @returns The mutation to add a new 2D image to the specified room.
 */
export async function createMutationAdd2dAttachmentFromFile({
  file,
  uploadedFileData,
  thumbnailUri,
  roomSectionId,
  attachmentGroupId,
  shouldCreateAttachmentsGroup,
  rootId,
  userId,
  newImg2dId = generateGUID(),
}: CreateMutationAdd2dAttachmentFromFileProps): Promise<MutationAdd2dAttachment> {
  const imageData = await getImageMetadata(file);
  const { width: imageWidth, height: imageHeight } =
    await parseImageFromFile(file);

  const { name } = file;
  const newImageElement: IElementImg2d = {
    type: IElementType.img2d,
    md5Hash: uploadedFileData.md5,
    fileSize: file.size,
    pixelWidth: imageWidth,
    pixelHeight: imageHeight,
    fileName: name,
    uri: uploadedFileData.downloadUrl,
    thumbnailUri,
    id: newImg2dId,
    rootId,
    root_Id: rootId,
    parentId: attachmentGroupId,
    typeHint: null,
    name,
    createdBy: userId,
    createdAt: imageData.captureDate.toISOString(),
    modifiedBy: userId,
    modifiedAt: imageData.captureDate.toISOString(),
  };

  return createMutationAdd2dAttachment(
    shouldCreateAttachmentsGroup ? roomSectionId : attachmentGroupId,
    newImageElement,
    shouldCreateAttachmentsGroup
      ? createIElement<IElementGroup>({
          type: IElementType.group,
          id: attachmentGroupId,
          parentId: roomSectionId,
          typeHint: IElementTypeHint.attachments,
          name: "Attached Photos",
          rootId,
          createdBy: userId,
          xOr: true,
        })
      : undefined,
  );
}

/**  Image metadata that is need to populate IElementImage360 element */
type ImageMetaData = {
  /** the original date/time an image was taken (will fall back to file's `lastModified` if EXIF fails or is missing) */
  captureDate: Date;

  /** GPS latitude value */
  latitude?: number;

  /** GPS longitude value */
  longitude?: number;
};

/**
 * Extract some metadata from a given image file.
 *
 * @param file - The image file (e.g. from <input type="file">)
 * @returns A `Promise<ImageMetaData>` representing the taken time or fallback to lastModified
 */
export async function getImageMetadata(file: File): Promise<ImageMetaData> {
  const isJpeg =
    file.type.toLowerCase().includes("jpeg") ||
    file.type.toLowerCase().includes("jpg");

  const isHeicHeif =
    file.type.toLowerCase().includes("heic") ||
    file.type.toLowerCase().includes("heif");

  if (!isJpeg && !isHeicHeif) {
    return { captureDate: new Date(file.lastModified) };
  }

  try {
    const data = await exifParse(file, { tiff: true, exif: true });
    return {
      captureDate:
        data?.DateTimeOriginal ??
        data?.CreateDate ??
        new Date(file.lastModified),
      latitude: data?.latitude,
      longitude: data?.longitude,
    };
  } catch {
    return { captureDate: new Date(file.lastModified) };
  }
}
